export const buildRenewalTicketCommand = (
  mensualidadId,
  renovacionId,
  operation = 'issue',
  snapshot = undefined,
  replacement = {}
) => {
  if (!mensualidadId || !renovacionId || !['issue', 'reprint', 'replace'].includes(operation)) {
    const error = new Error('La aplicación fue actualizada. Recargue la página.');
    error.code = 'renovacion_identity_required';
    throw error;
  }
  const command = {
    mensualidad_id: mensualidadId,
    renovacion_id: renovacionId,
    operation,
  };
  if (operation === 'replace') {
    if (!snapshot || !replacement.expectedTicketId || !replacement.operationId) {
      const error = new Error('Debe indicar los datos de la nueva versión.');
      error.code = 'ticket_replacement_required';
      throw error;
    }
    command.expected_ticket_id = replacement.expectedTicketId;
    command.operation_id = replacement.operationId;
  }
  if (operation !== 'reprint' && snapshot) command.snapshot = snapshot;
  return command;
};

export const buildMonthlyEntryTicketCommand = (
  mensualidadId,
  operation,
  snapshot = undefined,
  replacement = {}
) => {
  if (!mensualidadId || !['issue', 'reprint', 'replace'].includes(operation)) {
    const error = new Error('La aplicación fue actualizada. Recargue la página.');
    error.code = 'ticket_command_required';
    throw error;
  }
  if (['issue', 'replace'].includes(operation) && !snapshot) {
    const error = new Error('Debe indicar los datos de la nueva versión.');
    error.code = 'ticket_snapshot_required';
    throw error;
  }
  const command = {
    mensualidad_id: mensualidadId,
    operation,
  };
  if (operation === 'replace') {
    if (!replacement.expectedTicketId || !replacement.operationId) {
      const error = new Error('Debe indicar los datos de la nueva versión.');
      error.code = 'ticket_replacement_required';
      throw error;
    }
    command.expected_ticket_id = replacement.expectedTicketId;
    command.operation_id = replacement.operationId;
  }
  if (operation !== 'reprint') command.snapshot = snapshot;
  return command;
};

export const createTicketOperationId = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  if (typeof cryptoApi?.getRandomValues !== 'function') {
    throw new Error('Este navegador no puede generar una identificación segura para la operación.');
  }
  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const isTicketChangedError = (error) => (
  error?.response?.status === 409
  && error?.response?.data?.code === 'ticket_changed'
);

export const canonicalTicketErrorMessage = (error) => {
  const code = error?.response?.data?.code || error?.code;
  if (code === 'renovacion_identity_required' || code === 'ticket_command_required') {
    return 'La aplicación fue actualizada. Recargue la página.';
  }
  if (error?.response?.status === 401 || error?.response?.status === 403) {
    if (code === 'ticket_replace_forbidden') {
      return 'No tienes permiso para modificar este ticket.';
    }
    return 'La sesión expiró. Inicie sesión nuevamente.';
  }
  if (code === 'ticket_changed') {
    return 'Este ticket cambió desde que abriste la pantalla. Revisa la información actual antes de volver a modificarlo.';
  }
  if (code === 'monthly_ticket_legacy_conflict') {
    return 'Esta mensualidad tiene un ticket de entrada histórico pendiente de normalización. Actualice los datos e intente nuevamente o contacte al administrador.';
  }
  if (code === 'idempotency_key_reused') {
    return 'La operación ya fue utilizada con otros datos. Inicia una modificación nueva.';
  }
  if (code === 'no_changes') return 'No hay cambios para guardar.';
  if (code === 'ticket_annulled') return 'Un ticket anulado no se puede modificar.';
  if (code === 'movement_missing') {
    return 'El movimiento financiero asociado ya no existe. No se modificó el ticket.';
  }
  if ([
    'ticket_canonical_inconsistent',
    'ticket_history_inconsistent',
    'ticket_canonical_not_current',
    'ticket_current_count_inconsistent',
    'ticket_canonical_required',
    'ticket_canonical_changed',
    'monthly_ticket_legacy_conflict',
    'ticket_link_inconsistent',
    'renovacion_multiple_owner',
    'renovacion_no_pertenece',
    'movement_cashbox_inconsistent',
    'movement_required',
    'movement_financial_inconsistent',
    'ticket_chain_branched',
    'ticket_chain_cycle',
    'ticket_chain_multiple_current',
  ].includes(code)) {
    return 'No fue posible emitir el ticket. Actualice los datos e intente nuevamente.';
  }
  return error?.response?.data?.detail || error?.message || 'No fue posible emitir el ticket.';
};

export const annulMonthlyTicket = async ({
  apiUrl,
  ticketType,
  ticketId,
  httpClient,
  getRequestConfig,
}) => {
  const requestConfig = await getRequestConfig(apiUrl, httpClient.get);
  return httpClient.put(
    `${apiUrl}/tickets-${ticketType}/${ticketId}/`,
    { anulado: true },
    requestConfig
  );
};
